import axios, { AxiosResponse } from 'axios'
import type {
  AuthResponse,
  UserProfile as User,
  FactCheckRequest,
  FactCheckResponse,
  UserPreferences,
  UserStats,
  Subscription,
  SubscriptionTier
} from '@treu/shared'
import type { ChatThread, ChatMessage } from '../types'
import { updateGlobalAuthState } from './authState'

// API response meta type
interface ApiResponseMeta {
  error?: string
}

// Axios-like error type for proper error handling
interface AxiosLikeError {
  response?: {
    status: number
    statusText: string
    data?: {
      message?: string
    }
  }
  request?: unknown
  message?: string
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Requests that run a full verification pipeline (source aggregation + AI
 * analysis) routinely take 20-35s — the default 30s timeout aborted the
 * request client-side while the backend completed and persisted the verdict,
 * showing the user a false "failed" error and inviting a quota-burning
 * re-submit. Matches the 2-minute ceiling used for data export.
 */
const VERIFICATION_TIMEOUT_MS = 120000

// Cookie-based authentication - credentials automatically sent with requests
apiClient.defaults.withCredentials = true

// Handle auth errors and network issues
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status

      switch (status) {
        case 401:
          // Unauthorized - trigger global logout (handles deleted accounts from other platforms)
          // Skip for auth endpoints to avoid loops
          if (!error.config?.url?.includes('/auth/')) {
            updateGlobalAuthState({ status: 'unauthenticated' })
          }
          break
        case 403:
        case 429:
        case 500:
        case 502:
        case 503:
        case 504:
        default:
          // Errors handled by UI components
          break
      }
    } else if (error.request) {
      // Network error - no response received
      // Handled by UI components
    } else {
      // Request setup error
      // Handled by UI components
    }

    throw error
  }
)

export interface SocialLoginResponse {
  success: boolean
  data: {
    user: User
  }
}

export const authApi = {
  async socialLogin(
    provider: 'google' | 'apple',
    token: string,
    fullName?: { givenName?: string; familyName?: string }
  ): Promise<SocialLoginResponse> {
    const response: AxiosResponse<SocialLoginResponse> = await apiClient.post('/auth/social-login', {
      provider,
      token,
      fullName, // Only sent for Apple Sign-In (Apple provides name on first sign-in only)
    })
    return response.data
  },

  async refreshToken(): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await apiClient.post('/auth/refresh')
    return response.data
  },

  async logout(): Promise<void> {
    await apiClient.delete('/auth/logout')
  }
}

export const userApi = {
  async getProfile(): Promise<{ data: User }> {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.get('/user/profile')
    return response.data
  },

  async updateProfile(updates: Partial<User>): Promise<{ data: User }> {
    const response: AxiosResponse<{ success: boolean; data: User }> = await apiClient.put('/user/profile', updates)
    return response.data
  },

  async uploadAvatar(file: File): Promise<{ data: { profilePicture: string } }> {
    const formData = new FormData()
    formData.append('avatar', file)

    const response: AxiosResponse<{ success: boolean; data: { profilePicture: string } }> = await apiClient.post('/user/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async updatePreferences(preferences: UserPreferences): Promise<UserPreferences> {
    const response: AxiosResponse<{ success: boolean; data: UserPreferences } | UserPreferences> = await apiClient.put('/user/preferences', preferences)
    // Handle both old format (direct data) and new format (wrapped in success/data)
    if ('success' in response.data && 'data' in response.data) {
      return response.data.data
    }
    return response.data as UserPreferences
  },

  async getPreferences(): Promise<UserPreferences> {
    const response: AxiosResponse<{ success: boolean; data: UserPreferences }> = await apiClient.get('/user/preferences')
    return response.data.data || (response.data as unknown as UserPreferences)
  },

  async getStats(): Promise<UserStats> {
    const response: AxiosResponse<UserStats> = await apiClient.get('/user/stats')
    return response.data
  },

  async getVerificationHistory(): Promise<FactCheckResponse[]> {
    const response: AxiosResponse<FactCheckResponse[]> = await apiClient.get('/user/verifications')
    return response.data
  },

  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await apiClient.delete('/user/account')
    return response.data
  },

  // Streams the full account export from the backend. Returns both the Blob
  // (for download) and the filename suggested by the Content-Disposition header
  // so the caller can save it with a meaningful name.
  async exportData(): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get('/user/export', {
      responseType: 'blob',
      // Bigger exports can take a moment to assemble — bump the timeout above
      // the default 30s but keep it bounded.
      timeout: 120000,
    })

    const disposition = response.headers['content-disposition'] as string | undefined
    let filename = 'treu-data-export.json'
    if (disposition) {
      const match = /filename="?([^"]+)"?/.exec(disposition)
      if (match && match[1]) filename = match[1]
    }

    return { blob: response.data as Blob, filename }
  }
}


export const factCheckApi = {
  async verify(request: FactCheckRequest): Promise<FactCheckResponse> {
    // Validate request
    if (!request.query || typeof request.query !== 'string' || request.query.trim().length === 0) {
      throw new Error('Invalid request: query is required and must be a non-empty string')
    }
    
    if (request.query.trim().length > 5000) {
      throw new Error('Query is too long. Please limit to 5000 characters.')
    }
    
    try {
      const response: AxiosResponse<{ success: boolean; data: FactCheckResponse; meta: ApiResponseMeta }> = await apiClient.post('/fact-check', request)

      if (!response.data) {
        throw new Error('No response data received from server')
      }

      if (!response.data.success) {
        throw new Error(`API request failed: ${response.data.meta?.error || 'Unknown error'}`)
      }

      if (!response.data.data) {
        throw new Error('No verification data in response')
      }

      return response.data.data
    } catch (error) {
      // Re-throw with more context
      const axiosError = error as AxiosLikeError
      if (axiosError.response) {
        // Server responded with error status
        const status = axiosError.response.status
        const message = axiosError.response.data?.message || axiosError.response.statusText
        throw new Error(`Server error (${status}): ${message}`)
      } else if (axiosError.request) {
        // Request made but no response received
        throw new Error('Network Error: Unable to connect to verification service')
      } else {
        // Something else happened
        throw new Error(`Request failed: ${axiosError.message}`)
      }
    }
  },

  async getById(id: string): Promise<FactCheckResponse> {
    try {
      const response: AxiosResponse<{ success: boolean; data: FactCheckResponse; meta: ApiResponseMeta }> = await apiClient.get(`/fact-check/${id}`)

      if (!response.data) {
        throw new Error('No response data received from server')
      }

      if (!response.data.success) {
        throw new Error(`API request failed: ${response.data.meta?.error || 'Unknown error'}`)
      }

      if (!response.data.data) {
        throw new Error('No verification data in response')
      }

      return response.data.data
    } catch (error) {
      // Re-throw with more context
      const axiosError = error as AxiosLikeError
      if (axiosError.response) {
        // Server responded with error status
        const status = axiosError.response.status
        const message = axiosError.response.data?.message || axiosError.response.statusText
        throw new Error(`Server error (${status}): ${message}`)
      } else if (axiosError.request) {
        // Request made but no response received
        throw new Error('Network Error: Unable to connect to verification service')
      } else {
        // Something else happened
        throw new Error(`Request failed: ${axiosError.message}`)
      }
    }
  },

  // Submit thumbs / report feedback on a verification. Best-effort — a failure
  // here must never disrupt the user; callers swallow the rejection.
  async submitFeedback(
    factCheckId: string,
    signal: 'helpful' | 'not_helpful' | 'report',
    opts?: { reason?: string; messageId?: string }
  ): Promise<void> {
    await apiClient.post(`/fact-checks/${factCheckId}/feedback`, {
      signal,
      reason: opts?.reason,
      messageId: opts?.messageId,
    })
  }
}

export const communityApi = {
  async getStats() {
    const response = await apiClient.get('/community/stats')
    return response.data
  },

  async getPosts(limit = 20, offset = 0) {
    const response = await apiClient.get('/community/posts', {
      params: { limit, offset }
    })
    return response.data
  },

  async createPost(content: string) {
    const response = await apiClient.post('/community/posts', {
      content
    })
    return response.data
  },

  async votePost(postId: string, vote: 'up' | 'down'): Promise<{ upvotes: number; downvotes: number; userVote: string | null }> {
    const voteType = vote === 'up' ? 'upvote' : 'downvote'
    const response = await apiClient.post(`/community/posts/${postId}/vote`, {
      voteType
    })
    return response.data.data
  },

  async getComments(postId: string, limit = 50, offset = 0) {
    const response = await apiClient.get(`/community/posts/${postId}/comments`, {
      params: { limit, offset }
    })
    return response.data
  },

  async addComment(postId: string, content: string, parentCommentId?: string) {
    const response = await apiClient.post(`/community/posts/${postId}/comments`, {
      content,
      parentCommentId
    })
    return response.data
  },

  async deletePost(postId: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/community/posts/${postId}`)
    return response.data.data
  },

  async deleteComment(commentId: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/community/comments/${commentId}`)
    return response.data.data
  }
}

export const subscriptionApi = {
  async getSubscription(): Promise<Subscription> {
    const response: AxiosResponse<{ success: boolean; data: Subscription }> = await apiClient.get('/subscription')
    return response.data.data
  },

  async createCheckout(tier: SubscriptionTier): Promise<{ checkoutUrl: string }> {
    const response: AxiosResponse<{ success: boolean; data: { checkoutUrl: string } }> = await apiClient.post('/subscription/checkout', { tier })
    return response.data.data
  },

  async updateSubscription(tier: SubscriptionTier): Promise<{ message: string }> {
    const response: AxiosResponse<{ success: boolean; data: { message: string } }> = await apiClient.put('/subscription', { tier })
    return response.data.data
  },

  async cancelSubscription(): Promise<{ message: string }> {
    const response: AxiosResponse<{ success: boolean; data: { message: string } }> = await apiClient.delete('/subscription')
    return response.data.data
  },

  async getCustomerPortal(): Promise<{ customerPortalUrl: string; updatePaymentUrl: string }> {
    const response: AxiosResponse<{ success: boolean; data: { customerPortalUrl: string; updatePaymentUrl: string } }> = await apiClient.get('/subscription/portal')
    return response.data.data
  }
}

export const analyticsApi = {
  async getAnalytics() {
    const response = await apiClient.get('/analytics')
    return response.data
  }
}

export const shareApi = {
  /** Mint (or return) the public share link for a verdict the user owns. */
  async createShare(factCheckId: string): Promise<{ shortId: string; url: string }> {
    const response = await apiClient.post('/shares', { factCheckId })
    return response.data.data
  }
}

export const contactApi = {
  /** Public contact form for general questions. */
  async submit(payload: {
    name?: string
    email: string
    message: string
    /** Honeypot field — hidden from real users, submitted verbatim. */
    website?: string
  }): Promise<void> {
    await apiClient.post('/contact', payload)
  }
}

export const chatApi = {
  async getThreads(): Promise<ChatThread[]> {
    const response: AxiosResponse<ChatThread[]> = await apiClient.get('/chat/threads')
    return response.data
  },

  async createThread(): Promise<ChatThread> {
    const response: AxiosResponse<ChatThread> = await apiClient.post('/chat/threads')
    return response.data
  },

  async updateThread(threadId: string, title: string): Promise<ChatThread> {
    const response: AxiosResponse<ChatThread> = await apiClient.put(`/chat/threads/${threadId}`, { title })
    return response.data
  },

  async deleteThread(threadId: string): Promise<void> {
    await apiClient.delete(`/chat/threads/${threadId}`)
  },

  async getMessages(threadId: string): Promise<ChatMessage[]> {
    const response: AxiosResponse<ChatMessage[]> = await apiClient.get(`/chat/threads/${threadId}/messages`)
    return response.data
  },

  async addMessage(threadId: string, message_type: 'user' | 'bot' | 'typing', content?: string, result?: FactCheckResponse): Promise<ChatMessage> {
    // A user message can trigger a full verification server-side — see
    // VERIFICATION_TIMEOUT_MS.
    const response: AxiosResponse<ChatMessage> = await apiClient.post(`/chat/threads/${threadId}/messages`, {
      message_type,
      content,
      result
    }, { timeout: VERIFICATION_TIMEOUT_MS })
    return response.data
  },

  async deleteMessage(threadId: string, messageId: string): Promise<void> {
    await apiClient.delete(`/chat/threads/${threadId}/messages/${messageId}`)
  },

  async verifyPendingQuery(threadId: string, messageId: string): Promise<ChatMessage> {
    const response: AxiosResponse<ChatMessage> = await apiClient.post(
      `/chat/threads/${threadId}/messages/${messageId}/verify`,
      undefined,
      { timeout: VERIFICATION_TIMEOUT_MS }
    )
    return response.data
  }
}

export default apiClient